import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { richTextToHTML } from "src/clients/uniform-client/enhancers/utils";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { templateBenefitsContentfulRelayFragment$key } from "@/__generated_contentful__/templateBenefitsContentfulRelayFragment.graphql";
import { templateGuideContentfulRelayFragment$key } from "@/__generated_contentful__/templateGuideContentfulRelayFragment.graphql";
import { templateTabbedSectionContentfulRelayFragment$key } from "@/__generated_contentful__/templateTabbedSectionContentfulRelayFragment.graphql";
import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4TabPanel,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { SanitizedRichText } from "@/components/confluence/util/sanitized-rich-text";
import wacToken from "@/libraries/design/foundations/token/src/wac-token.tsx";

import { tabbedSectionHeading } from "../../../../src/template-details-page/template-benefits-section.tsx";
import { TemplateBenefits } from "../../components/contentful-relay-components/template-benefits-contentful-relay";
import { TemplateGuide } from "../../components/contentful-relay-components/template-guide-contentful-relay";

const tabStyles = css({
    "backgroundColor": wacToken("color.neutral.light200"),
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "flex",
    "textAlign": "center",
    "textDecoration": "none",
    "padding": `${token("space.100")} ${token("space.400")} ${token(
        "space.100"
    )} ${token("space.400")}`,
    "borderRadius": wacToken("corner.radius.pill"),
    "cursor": "pointer",
    "border": `2px solid ${wacToken("color.neutral.light200")}`,
    "fontSize": "18px",
    ":hover": {
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.

        border: `2px solid ${wacToken("color.primary.blue")}`, // #0C66E4
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.

        backgroundColor: wacToken("color.extended.blue100"), // #E9F2FE
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.

        color: wacToken("color.primary.blue") // #0C66E4
    }
});

const currentStyle = css({
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.

    border: `2px solid ${wacToken("color.primary.blue")}`, // #0C66E4
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.

    backgroundColor: wacToken("color.extended.blue100"), // #E9F2FE
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.

    color: wacToken("color.primary.blue") // #0C66E4
});

const tabSectionStyle = css({
    marginBlockStart: "56px"
});

const sectionStyles = css({
    color: wacToken("color.extended.blue900"),
    paddingBlockStart: "64px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    [media.below.sm]: {
        overflow: "scroll",
        width: "100%"
    }
});

const styles = css({
    display: "flex",
    [media.above.sm]: {
        justifyContent: "center"
    }
});

const TabHeading = ({
    title,
    isSelected
}: {
    title?: string;
    isSelected?: boolean;
}) => {
    return (
        <div css={[tabStyles, isSelected && currentStyle]} className="h5">
            {title}
        </div>
    );
};

interface TemplateTabbedSectionProps {
    template: templateTabbedSectionContentfulRelayFragment$key;
}

const TemplateTabbedSectionRelayFragment = graphql`
    fragment templateTabbedSectionContentfulRelayFragment on ProductTemplate {
        templateOverviewCollection {
            items {
                overviewTitle
                overviewDescription {
                    json
                }
            }
        }
        templateBenefitsCollection {
            items {
                ...templateBenefitsContentfulRelayFragment
            }
        }
        howToUseThisTemplateCollection {
            items {
                ...templateGuideContentfulRelayFragment
            }
        }
        templateProductRationaleCollection {
            items {
                rationaleTitle
                templateProductRationaleDescription {
                    json
                }
            }
        }
    }
`;

// eslint-disable-next-line complexity
export const TemplateTabbedSection = ({
    template: templateRef
}: TemplateTabbedSectionProps) => {
    const intl = useIntl();

    const template = useFragment(
        TemplateTabbedSectionRelayFragment,
        templateRef
    );

    const tabs = [
        {
            id: "tab1",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.overview.cfind.1508",
                defaultMessage: "Overview"
            }),
            hide: !template.templateOverviewCollection?.items[0],
            component: (
                <section css={tabSectionStyle}>
                    {template.templateOverviewCollection?.items?.map(
                        (overview, index) => (
                            <section key={`overview-${index}`}>
                                <h2 css={tabbedSectionHeading}>
                                    {overview?.overviewTitle}
                                </h2>
                                {overview?.overviewDescription && (
                                    <SanitizedRichText
                                        innerHTML={richTextToHTML(
                                            overview?.overviewDescription.json
                                        )}
                                    />
                                )}
                            </section>
                        )
                    )}
                </section>
            )
        },
        {
            id: "tab2",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.benefits.cfind.1508",
                defaultMessage: "Benefits"
            }),
            hide:
                !template.templateBenefitsCollection?.items ||
                template.templateBenefitsCollection?.items.length === 0,
            component: (
                <section>
                    {template.templateBenefitsCollection?.items?.map(
                        (templateBenefitsContainer, index) => (
                            <TemplateBenefits
                                key={`benefits-${index}`}
                                templateBenefits={
                                    templateBenefitsContainer as unknown as templateBenefitsContentfulRelayFragment$key
                                }
                            />
                        )
                    )}
                </section>
            )
        },
        {
            id: "tab3",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.howTo.cfind.1508",
                defaultMessage: "How to"
            }),
            hide:
                !template.howToUseThisTemplateCollection?.items ||
                template.howToUseThisTemplateCollection?.items.length === 0,
            component: (
                <section>
                    {template.howToUseThisTemplateCollection?.items?.map(
                        (templateGuide, index) => (
                            <TemplateGuide
                                key={`how2use-${index}`}
                                templateGuide={
                                    templateGuide as unknown as templateGuideContentfulRelayFragment$key
                                }
                            />
                        )
                    )}
                </section>
            )
        },
        {
            id: "tab4",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.whyConfluence.cfind.1508",
                defaultMessage: "Why Confluence?"
            }),
            // temporary content for demoing... will be replaced with content from template object
            hide: !template.templateProductRationaleCollection?.items,

            component: (
                <section css={tabSectionStyle}>
                    {template.templateProductRationaleCollection?.items?.map(
                        (rationale, index) => (
                            <section key={`rationale-${index}`}>
                                <h2 css={tabbedSectionHeading}>
                                    {rationale?.rationaleTitle}
                                </h2>
                                {rationale?.templateProductRationaleDescription && (
                                    <SanitizedRichText
                                        innerHTML={richTextToHTML(
                                            rationale
                                                ?.templateProductRationaleDescription
                                                .json
                                        )}
                                    />
                                )}
                            </section>
                        )
                    )}
                </section>
            )
        }
    ];

    const renderTabbedSection = () => {
        return (
            <Adg4Tabs>
                <Adg4DraggableTabList css={styles}>
                    {tabs
                        .filter((tab) => !tab.hide)
                        .map((tab) => (
                            <Adg4DraggableTab key={tab.id} title={tab.title}>
                                <TabHeading title={tab.title} />
                            </Adg4DraggableTab>
                        ))}
                </Adg4DraggableTabList>
                {tabs
                    .filter((tab) => !tab.hide)
                    .map((tab, index) => (
                        <Adg4TabPanel key={index} keepInDom={true}>
                            <>{tab.component}</>
                        </Adg4TabPanel>
                    ))}
            </Adg4Tabs>
        );
    };

    return (
        <section data-testid="template-tabbed-section" css={[sectionStyles]}>
            {renderTabbedSection()}
        </section>
    );
};
